import { useMutation, useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { JobPostingDetailVO } from '@domain/vo/common/JobPostingDetailVO';
import { SimilarPositionVO } from '@domain/vo/common/SimilarPositionVO';
import { RemoteIndexRepo } from '@repository/index/IndexRepo';
import CurationPositionVO from '@domain/vo/index/CurationPositionVO';
import CurationCategoryVO from '@domain/vo/index/CurationCategoryVO';
import { AdsTitleType } from '@domain/constant/AdsTitleType';

const remoteIndexRepo = new RemoteIndexRepo();

export const keys = ['index'];

export const fetchPositionJobGroups = async (pageSize: number, jobGroupCode?: number) => {
  if (!jobGroupCode) return;

  const { data } = await remoteIndexRepo.fetchPositionJobGroups(jobGroupCode, pageSize);
  return data;
};

const fetchJobPostingSimilarPosition = async (jobPostingSn: number, pageSize = 6) => {
  const { data } = await remoteIndexRepo.fetchJobPostingSimilarPosition(jobPostingSn, pageSize);
  return data.jobPostings.map((item) => new SimilarPositionVO(item));
};

const fetchJobPostingDetail = async (jobPostingSn: number | null) => {
  if (!jobPostingSn) return;

  const { data } = await remoteIndexRepo.fetchJobPostingDetail(jobPostingSn);
  return new JobPostingDetailVO(data);
};

export const fetchJobPostingCurationPosition = async (curationCategorySn?: number) => {
  if (!curationCategorySn) return [];

  const { data } = await remoteIndexRepo.fetchJobPostingCurationPosition(curationCategorySn);
  return data.jobPostings.map((item) => new CurationPositionVO(item));
};

export const fetchJobPostingCurationCategories = async () => {
  const { data } = await remoteIndexRepo.fetchJobPostingCurationCategories();
  return data.categories.map((item) => new CurationCategoryVO(item));
};

export const fetchJobPostingRecommendPosition = async (page: number, size: number) => {
  const { data } = await remoteIndexRepo.fetchJobPostingRecommendPosition(page, size);

  // TODO view에서 랜덤 처리 필요
  const length = data?.jobPostings?.length || 0;
  const randomSortList = data.jobPostings.slice(0, length).sort(() => Math.random() - 0.5);
  const remainList = data.jobPostings.slice(length);

  return [...randomSortList, ...remainList];
};

export const fetchAccPostingPosition = async (pageSize: number) => {
  const { data } = await remoteIndexRepo.fetchAccPostingPosition(pageSize);
  return data;
};

export const fetchMainBanners = async () => {
  const { data } = await remoteIndexRepo.fetchMainBanners();
  return data;
};

const fetchMainBannerClicksLog = async (mainBannerSn: number) => {
  const { data } = await remoteIndexRepo.fetchMainBannerClicksLog(mainBannerSn);
  return data;
};

const fetchMainBannerShowsLog = async (mainBannerSn: number) => {
  const { data } = await remoteIndexRepo.fetchMainBannerShowsLog(mainBannerSn);
  return data;
};

export const fetchAdsTitle = async (type: AdsTitleType) => {
  const { data } = await remoteIndexRepo.fetchAdsTitle(type);
  return data;
};

const fetchJobpostingBookmark = async (jobPostingSn: number) => {
  return await remoteIndexRepo.fetchJobpostingBookmark(jobPostingSn);
};

const deleteJobpostingBookmark = async (jobPostingSn: number) => {
  return await remoteIndexRepo.deleteJobpostingBookmark(jobPostingSn);
};
// == use Query ===

export function usePositionJobGroups(jobGroupCode?: number, pageSize = 9) {
  return useQuery(
    [...keys, 'PositionList', 'JobGroups', jobGroupCode, pageSize],
    () => fetchPositionJobGroups(pageSize, jobGroupCode),
    {
      enabled: !!jobGroupCode,
    },
  );
}

export function useJobPostingSimilarPosition(jobGroupCode: number, pageSize = 6) {
  return useQuery([...keys, 'jobPosting', 'PositionList', 'similar', jobGroupCode, pageSize], () =>
    fetchJobPostingSimilarPosition(jobGroupCode, pageSize),
  );
}

export function useJobPostingDetail(jobGroupCode: number | null) {
  return useQuery([...keys, 'jobPosting', 'detail', jobGroupCode], () => fetchJobPostingDetail(jobGroupCode), {
    enabled: !!jobGroupCode,
    cacheTime: 0, //최신 데이터를 보여주기 위함.
  });
}

export function useJobPostingCurationPosition(curationCategorySn?: number) {
  return useQuery(
    [...keys, 'PositionList', 'Curation', curationCategorySn],
    () => fetchJobPostingCurationPosition(curationCategorySn),
    {
      enabled: !!curationCategorySn,
    },
  );
}

export function useJobPostingCurationCategories() {
  return useQuery([...keys, 'categoryList', 'Curation'], fetchJobPostingCurationCategories);
}

export function useJobPostingRecommendPosition(page: number, size: number) {
  return useQuery([...keys, 'jobPosting', 'PositionList', 'recommend', page, size], () =>
    fetchJobPostingRecommendPosition(page, size),
  );
}

export function useAccPostingPosition(pageSize: number) {
  return useQuery([...keys, 'acc', 'PositionList', pageSize], () => fetchAccPostingPosition(pageSize));
}

export function useMainBanners() {
  return useQuery([...keys, 'mainBannerList'], fetchMainBanners);
}

export function useAdsTitle(type: AdsTitleType) {
  return useQuery([...keys, 'ads', 'title', type], () => fetchAdsTitle(type));
}

export function useAddBookMark(onSuccess: () => void) {
  return useMutation((jobPostingSn: number) => fetchJobpostingBookmark(jobPostingSn), {
    onSuccess,
  });
}

export function useDeleteBookMark(onSuccess: () => void) {
  return useMutation((jobPostingSn: number) => deleteJobpostingBookmark(jobPostingSn), {
    onSuccess,
  });
}
// == use Mutation ===

export function useMainBannerClicksLog() {
  return useMutation((mainBannerSn: number) => fetchMainBannerClicksLog(mainBannerSn), {
    onError: (err: AxiosError) => {
      console.error(err.response?.data.message);
      return;
    },
  });
}

export function useMainBannerShowsLog() {
  return useMutation((mainBannerSn: number) => fetchMainBannerShowsLog(mainBannerSn), {
    onError: (err: AxiosError) => {
      console.error(err.response?.data.message);
      return;
    },
  });
}
